import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import {
  Button,
  Chip,
  Input,
  RichTextEditor,
  Select,
} from '@/components/ui'
import { EmailThreadPanel, SignaturePicker } from '@/shell/services/email-thread'
import { applySignatureToBody } from '@/lib/utils/signature'
import { useHasAnyPermission } from '@/shell/module-system'
import {
  useArchiveTicket,
  useMarkResponded,
  useMoveStage,
  usePatchTicket,
  usePreviewTemplate,
  useRefreshOdoo,
  useSendResponse,
  useStages,
  useTemplates,
  useTicketDetail,
  useUnarchiveTicket,
} from '../api/queries'
import type { Assignee, OdooContext, Priority, TicketDetail } from '../api/types'
import {
  ASSIGNEE_NAMES,
  ASSIGNEE_ORDER,
  CATEGORY_LABELS,
  PRIORITY_OPTIONS,
  PRIORITY_VARIANT,
} from '../utils/meta'
import { SupportTimeline } from './SupportTimeline'

export interface SupportTicketWorkViewProps {
  ticketId: string
}

/**
 * Full-page per-ticket working view (email-thread-viewer sprint).
 *
 * Replaces the slide-out SupportDetailDrawer for the "work a ticket" role.
 * Header band with back-to-board link + a 2-column grid: LEFT the linked
 * MailDesk conversation, RIGHT the work panel (stage/priority/assignee/SLA,
 * Odoo context, CARLA draft + send, internal notes, history).
 */
export function SupportTicketWorkView({ ticketId }: SupportTicketWorkViewProps) {
  const canEdit = useHasAnyPermission([
    'admin', 'operations', 'purchasing', 'sales', 'regulatory', 'finance',
  ])
  const { data, isLoading } = useTicketDetail(ticketId)
  const stages = useStages()
  const patch = usePatchTicket()
  const move = useMoveStage()
  const archive = useArchiveTicket()
  const unarchive = useUnarchiveTicket()

  if (isLoading || !data) {
    return (
      <div data-density="comfortable" className="flex h-full flex-col">
        <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
          <Link to="/support">
            <Button size="sm" variant="ghost">
              <ArrowLeft size={14} />
              All tickets
            </Button>
          </Link>
          <span className="text-role-section-label">Support ticket</span>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-8 text-tertiary">
          {isLoading ? 'Loading ticket…' : 'Ticket not found.'}
        </div>
      </div>
    )
  }

  const stageItems = stages.data?.items ?? []
  const editable = canEdit && !data.is_archived

  return (
    <div data-density="comfortable" className="flex h-full flex-col bg-canvas">
      <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-6 py-3">
        <Link to="/support">
          <Button size="sm" variant="ghost">
            <ArrowLeft size={14} />
            All tickets
          </Button>
        </Link>
        <div className="min-w-0">
          <h1 className="text-role-data-primary truncate">
            {data.ticket_number} · {data.title}
          </h1>
        </div>
        <div className="ml-2 flex flex-wrap items-center gap-2">
          <Chip size="xs" variant="neutral">
            {CATEGORY_LABELS[data.category]}
          </Chip>
          <Chip size="xs" variant={PRIORITY_VARIANT[data.priority]}>
            {data.priority}
          </Chip>
          {data.is_sla_breached ? (
            <Chip size="xs" variant="danger" dot>
              SLA breached
            </Chip>
          ) : null}
        </div>
        {canEdit ? (
          <div className="ml-auto flex items-center gap-2">
            {data.is_archived ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => unarchive.mutate(data.id)}
                loading={unarchive.isPending}
              >
                Unarchive
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const reason = window.prompt('Archive / resolve reason?') ?? undefined
                  archive.mutate({ id: data.id, reason })
                }}
                loading={archive.isPending}
              >
                Archive
              </Button>
            )}
          </div>
        ) : null}
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_minmax(380px,460px)]">
        {/* LEFT — linked MailDesk conversation */}
        <div className="min-h-0 overflow-y-auto border-r border-border-subtle">
          <EmailThreadPanel
            endpoint={`/api/support/tickets/${ticketId}/email`}
            latestMessageIndexId={data.source_message_index_id}
          />
        </div>

        {/* RIGHT — work panel */}
        <div className="min-h-0 overflow-y-auto bg-surface px-4 py-4">
          <div className="flex flex-col gap-4">
            {/* Editable fields */}
            <section className="grid grid-cols-2 gap-3">
              <Field label="Stage">
                <Select
                  value={data.stage_id ?? ''}
                  disabled={!editable || move.isPending}
                  onChange={(e) => move.mutate({ id: data.id, targetStageId: e.target.value })}
                >
                  {stageItems.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Priority">
                <Select
                  value={data.priority}
                  disabled={!editable}
                  onChange={(e) =>
                    patch.mutate({
                      id: data.id,
                      edits: { priority: e.target.value as Priority },
                    })
                  }
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Assigned to">
                <Select
                  value={data.assigned_to ?? ''}
                  disabled={!editable}
                  onChange={(e) =>
                    patch.mutate({
                      id: data.id,
                      edits: { assigned_to: (e.target.value || null) as Assignee | null },
                    })
                  }
                >
                  <option value="">Unassigned</option>
                  {ASSIGNEE_ORDER.map((a) => (
                    <option key={a} value={a}>
                      {ASSIGNEE_NAMES[a]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="SLA due">
                <div className="flex h-9 items-center text-sm text-secondary">
                  {data.sla_due_at ? (
                    <span className={data.is_sla_breached ? 'font-medium text-danger' : ''}>
                      {formatTimestamp(data.sla_due_at)}
                    </span>
                  ) : (
                    '—'
                  )}
                </div>
              </Field>
            </section>

            {data.secondary_assigned_to_name ? (
              <p className="text-role-metadata text-tertiary">
                Secondary: {data.secondary_assigned_to_name}
              </p>
            ) : null}

            {/* Customer + source */}
            <section className="flex flex-col gap-1 border-t border-border-subtle pt-3">
              <Row label="Customer" value={data.partner_name} />
              {data.source_email_from ? (
                <Row label="From" value={data.source_email_from} />
              ) : null}
              {data.source_email_subject ? (
                <Row label="Subject" value={data.source_email_subject} />
              ) : null}
            </section>

            {/* Odoo context */}
            <OdooContextPanel ticket={data} canEdit={editable} />

            {/* CARLA draft + send */}
            {editable ? <SendResponseSection ticket={data} /> : null}

            {/* Internal notes */}
            <section className="border-t border-border-subtle pt-3">
              <NotesEditor
                key={data.id}
                ticketId={data.id}
                initial={data.internal_notes ?? ''}
                disabled={!editable}
              />
            </section>

            {/* History */}
            <section className="border-t border-border-subtle pt-3">
              <h3 className="text-role-section-label mb-2">Activity</h3>
              <SupportTimeline entries={data.history} />
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

function OdooContextPanel({ ticket, canEdit }: { ticket: TicketDetail; canEdit: boolean }) {
  const refresh = useRefreshOdoo()
  const ctx: OdooContext = ticket.carla_odoo_context ?? {}
  const hasAny =
    ctx.so_found || ctx.lot_found || ctx.delivery_state || ctx.invoice_state
  return (
    <section className="border-t border-border-subtle pt-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-role-section-label">Odoo context</h3>
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            loading={refresh.isPending}
            onClick={() =>
              refresh.mutate(ticket.id, {
                onSuccess: () => toast.success('Odoo context refreshed'),
                onError: (e) => toast.error(String(e)),
              })
            }
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            Refresh
          </Button>
        ) : null}
      </div>
      {hasAny ? (
        <div className="flex flex-col gap-1">
          {ticket.sale_order_refs?.length ? (
            <Row label="SO" value={`${ticket.sale_order_refs.join(', ')}${ctx.so_state ? ` · ${ctx.so_state}` : ''}`} />
          ) : null}
          {ctx.delivery_state ? (
            <Row label="Delivery" value={`${ticket.delivery_refs?.join(', ') ?? ''} · ${ctx.delivery_state}`} />
          ) : null}
          {ctx.tracking_number ? (
            <Row label="Tracking" value={`${ctx.tracking_number}${ctx.carrier ? ` (${ctx.carrier})` : ''}`} />
          ) : null}
          {ctx.lot_found ? (
            <Row label="Lot" value={`${ticket.lot_refs?.join(', ') ?? ''}${ctx.lot_expiry ? ` · exp ${ctx.lot_expiry}` : ''}${ctx.lot_product ? ` · ${ctx.lot_product}` : ''}`} />
          ) : null}
          {ctx.invoice_state ? (
            <Row
              label="Invoice"
              value={`${ticket.invoice_refs?.join(', ') ?? ''} · ${ctx.invoice_state}${
                ctx.invoice_amount_residual != null
                  ? ` · $${ctx.invoice_amount_residual.toLocaleString()}`
                  : ''
              }${ctx.invoice_due ? ` · due ${ctx.invoice_due}` : ''}`}
            />
          ) : null}
        </div>
      ) : (
        <p className="text-role-metadata text-tertiary">
          No Odoo records resolved yet. Add an SO/lot/invoice ref and refresh.
        </p>
      )}
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-role-metadata text-tertiary">{label}</span>
      {children}
    </label>
  )
}

function Row({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="w-32 shrink-0 text-role-metadata text-tertiary">{label}</span>
      <span className="text-secondary">{value || '—'}</span>
    </div>
  )
}

function NotesEditor({
  ticketId,
  initial,
  disabled,
}: {
  ticketId: string
  initial: string
  disabled: boolean
}) {
  const [value, setValue] = useState(initial)
  const patch = usePatchTicket()
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-role-section-label">Internal notes</h3>
      <textarea
        className="min-h-[64px] w-full rounded-sm border border-border-default bg-surface p-2 text-sm text-primary"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
      />
      {!disabled && value !== initial ? (
        <div>
          <Button
            size="sm"
            variant="secondary"
            loading={patch.isPending}
            onClick={() => patch.mutate({ id: ticketId, edits: { internal_notes: value } })}
          >
            Save notes
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function SendResponseSection({ ticket }: { ticket: TicketDetail }) {
  const templates = useTemplates(ticket.category)
  const [templateId, setTemplateId] = useState('')
  const [email, setEmail] = useState(ticket.source_email_from ?? '')
  const [subject, setSubject] = useState(ticket.carla_draft_subject ?? '')
  const [body, setBody] = useState(ticket.carla_draft_body ?? '')
  const previewMut = usePreviewTemplate()
  const send = useSendResponse()
  const markResponded = useMarkResponded()

  const applyTemplate = async (tid: string) => {
    setTemplateId(tid)
    if (!tid) return
    try {
      const out = await previewMut.mutateAsync({ templateId: tid, ticketId: ticket.id })
      setSubject(out.subject)
      setBody(out.body)
    } catch (e) {
      toast.error(String(e))
    }
  }

  return (
    <section className="flex flex-col gap-2 border-t border-border-subtle pt-3">
      <h3 className="text-role-section-label">CARLA draft response</h3>
      <div className="grid grid-cols-2 gap-2">
        <Select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
          <option value="">Use template…</option>
          {(templates.data?.items ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        <Input
          type="email"
          placeholder="recipient@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <Input
        placeholder="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <RichTextEditor value={body} onChange={setBody} placeholder="Response body" />
      <div className="flex items-center gap-2">
        <span className="text-role-metadata text-tertiary">Signature</span>
        <SignaturePicker
          endpoint={`/api/support/tickets/${ticket.id}/signatures`}
          onApply={(sig) => setBody((b) => applySignatureToBody(b, sig))}
          applyDefaultOnLoad
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={!email || !subject || !body}
          loading={send.isPending}
          onClick={() => {
            if (
              !window.confirm(
                `Send to ${email} via support@siliconesplus.com?`,
              )
            )
              return
            send.mutate(
              { id: ticket.id, target_email: email, subject, body },
              {
                onSuccess: () => toast.success(`Response sent to ${email}`),
                onError: (e) => toast.error(String(e)),
              },
            )
          }}
        >
          Send
        </Button>
        <Button
          size="sm"
          variant="secondary"
          loading={markResponded.isPending}
          onClick={() =>
            markResponded.mutate(
              { id: ticket.id },
              {
                onSuccess: () => toast.success('Marked responded'),
                onError: (e) => toast.error(String(e)),
              },
            )
          }
        >
          Mark responded
        </Button>
      </div>
    </section>
  )
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`
  } catch {
    return iso
  }
}
